import type { MusicBands } from '../audio/MusicEngine';

export type VizFrame = {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
  t: number;
  bands: MusicBands;
  freq: Uint8Array;
  wave: Float32Array;
  title: string;
  artist: string;
  cover: HTMLImageElement | null;
  recording: boolean;
};

export type Visualizer = {
  id: string;
  label: string;
  draw: (frame: VizFrame) => void;
};

export function clearBg(ctx: CanvasRenderingContext2D, w: number, h: number, alpha = 1): void {
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = `rgba(5, 5, 5, ${alpha})`;
  ctx.fillRect(0, 0, w, h);
}

export function drawHud(frame: VizFrame): void {
  const { ctx, w, h, title, artist, bands, cover, recording } = frame;
  const pad = Math.max(16, Math.round(w * 0.02));
  const coverSize = Math.max(56, Math.round(w * 0.08));

  ctx.save();
  // Cover
  if (cover && cover.complete && cover.naturalWidth > 0) {
    const x = pad;
    const y = pad;
    ctx.save();
    roundRectPath(ctx, x, y, coverSize, coverSize, 10);
    ctx.clip();
    ctx.drawImage(cover, x, y, coverSize, coverSize);
    ctx.restore();
    ctx.strokeStyle = 'rgba(255, 77, 109, 0.35)';
    ctx.lineWidth = 2;
    roundRectPath(ctx, x, y, coverSize, coverSize, 10);
    ctx.stroke();
  }

  const textX = cover && cover.complete && cover.naturalWidth > 0 ? pad + coverSize + 14 : pad;
  ctx.textBaseline = 'top';
  ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
  ctx.font = `600 ${Math.max(14, Math.round(w * 0.018))}px 'Space Grotesk', ui-sans-serif, system-ui, sans-serif`;
  ctx.fillText(title.slice(0, 64), textX, pad);
  ctx.fillStyle = 'rgba(255, 133, 155, 0.95)';
  ctx.font = `500 ${Math.max(12, Math.round(w * 0.014))}px 'Space Grotesk', ui-sans-serif, system-ui, sans-serif`;
  ctx.fillText(artist.slice(0, 48), textX, pad + Math.max(22, Math.round(w * 0.024)));

  // energy meter
  const mw = Math.min(220, w * 0.18);
  const mh = 6;
  const mx = w - pad - mw;
  const my = pad + 8;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
  ctx.fillRect(mx, my, mw, mh);
  ctx.fillStyle = 'rgba(255, 77, 109, 0.9)';
  ctx.fillRect(mx, my, mw * bands.energy, mh);

  // REC badge
  if (recording) {
    const bx = w - pad - 72;
    const by = h - pad - 28;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    roundRectPath(ctx, bx - 8, by - 6, 80, 28, 8);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = `rgba(255, 51, 102, ${0.75 + bands.beat * 0.25})`;
    ctx.arc(bx + 6, by + 8, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.font = `700 ${Math.max(12, Math.round(w * 0.012))}px 'Space Mono', monospace`;
    ctx.textBaseline = 'middle';
    ctx.fillText('REC', bx + 20, by + 8);
  }

  ctx.restore();
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}
